'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image'; // ALLOW-RAW-IMG: static prize-pool trophy art + podium medals, not user content
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { SilkSheet } from '@/components/ui/silk-sheet';

// Frontend-only launch prize pool — a fixed pre-launch marketing figure. There is
// no backend: eligibility, raffle draw, and payouts are a post-launch concern and
// deliberately not modelled. This is static display copy.
const PRIZE_POOL = {
  totalPoolUsd: '10,000',
  splitTable: [{
    rankFrom: 1,
    rankTo: 1,
    shareUsd: '1,200'
  }, {
    rankFrom: 2,
    rankTo: 2,
    shareUsd: '900'
  }, {
    rankFrom: 3,
    rankTo: 3,
    shareUsd: '700'
  }, {
    rankFrom: 4,
    rankTo: 4,
    shareUsd: '500'
  }, {
    rankFrom: 5,
    rankTo: 5,
    shareUsd: '400'
  }, {
    rankFrom: 6,
    rankTo: 10,
    shareUsd: '300'
  }, {
    rankFrom: 11,
    rankTo: 20,
    shareUsd: '160'
  }, {
    rankFrom: 21,
    rankTo: 35,
    shareUsd: '60'
  }, {
    rankFrom: 36,
    rankTo: 50,
    shareUsd: '20'
  }],
  raffle: {
    winners: 20,
    poolUsd: '2,000'
  }
} as const;

/**
 * `$10,000 prize pool` banner — the gold-trophy earn incentive. Rendered on the
 * rewards dashboard (to lift the
 * "Start questing" click-through). Uses the STANDARD Panel surface with a
 * decorative gold "spotlight" overlay (the `morfi-prize-pool*` block in
 * globals.css). The ⓘ opens the prize breakdown modal.
 */
export function PrizeBanner(t0) {
  const $ = _c(23);
  const {
    onOpen
  } = t0;
  const tr = useTranslations("rewards");
  const amount = `$${PRIZE_POOL.totalPoolUsd}`;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="morfi-prize-pool__floor" aria-hidden={true} />;
    t2 = <div className="morfi-prize-pool__halo" aria-hidden={true} />;
    t3 = <div className="morfi-prize-pool__edge" aria-hidden={true} />;
    t4 = <div className="morfi-prize-pool__grain" aria-hidden={true} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="morfi-prize-pool__ring"><div className="morfi-prize-pool__disc"><Image src="/images/rewards/prize-pool.png" alt="" width={40} height={40} className="size-10 object-contain" aria-hidden={true} /></div></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== tr) {
    t6 = tr("prizeBanner.title", {
      amount
    });
    $[5] = tr;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== tr) {
    t7 = tr("prizeBanner.learnMore");
    $[7] = tr;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon icon={InformationCircleIconSolid} className="size-3" />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== onOpen || $[11] !== t7) {
    t9 = <button type="button" data-testid="prize-open-btn" aria-haspopup="dialog" aria-label={t7} onClick={onOpen} className="inline-flex shrink-0 cursor-pointer text-foreground-tertiary transition-colors hover:text-foreground-secondary">{t8}</button>;
    $[10] = onOpen;
    $[11] = t7;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t6 || $[14] !== t9) {
    t10 = <span className="flex items-center gap-1.5 text-body font-medium text-foreground-primary">{t6}{t9}</span>;
    $[13] = t6;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== tr) {
    t11 = tr("prizeBanner.description");
    $[16] = tr;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <span className="text-caption text-foreground-secondary">{t11}</span>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <Panel data-testid="prize-banner" className="morfi-prize-pool relative overflow-hidden">{t1}{t2}{t3}{t4}<PanelContent className="relative z-[3] flex items-center gap-3.5">{t5}<div className="flex min-w-0 flex-col gap-0.5 break-words text-left">{t10}{t12}</div></PanelContent></Panel>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
}
export function PrizeModal(t0) {
  const $ = _c(44);
  const {
    open,
    onOpenChange,
    onOpenTerms
  } = t0;
  const tr = useTranslations("rewards");
  const amount = `$${PRIZE_POOL.totalPoolUsd}`;
  let t1;
  if ($[0] !== tr) {
    t1 = tr("prizeModal.title");
    $[0] = tr;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tr) {
    t2 = tr("prizeModal.subtitle", {
      amount
    });
    $[2] = tr;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tr) {
    t3 = tr("prizeModal.positionHeader");
    $[4] = tr;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span>{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== tr) {
    t5 = tr("prizeModal.rewardHeader");
    $[8] = tr;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span>{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="flex items-center justify-between px-1 text-micro font-medium uppercase tracking-wide text-foreground-tertiary">{t4}{t6}</div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== tr) {
    t8 = PRIZE_POOL.splitTable.map(s => {
      const single = s.rankFrom === s.rankTo;
      const medal = single && s.rankFrom <= 3 ? s.rankFrom : null;
      return <div key={`${String(s.rankFrom)}-${String(s.rankTo)}`} className="flex items-center justify-between gap-2 rounded-panel-row border bg-background-tertiary px-4 py-3"><span className="flex items-center gap-2 text-body text-foreground-secondary">{medal !== null ? <Image src={`/images/social/podium/rank-${String(medal)}.png`} alt="" width={24} height={24} className="size-6 shrink-0 object-contain" aria-hidden={true} /> : null}{single ? tr("prizeModal.rankSingle", {
            rank: s.rankFrom
          }) : tr("prizeModal.rankRange", {
            from: s.rankFrom,
            to: s.rankTo
          })}</span><span className="text-body font-medium text-foreground-brand dark:text-white">${s.shareUsd}</span></div>;
    });
    $[15] = tr;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== tr) {
    t9 = tr("prizeModal.raffle", {
      winners: PRIZE_POOL.raffle.winners
    });
    $[17] = tr;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <span className="text-body font-medium text-foreground-primary">{t9}</span>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-body font-medium text-foreground-brand dark:text-white">${PRIZE_POOL.raffle.poolUsd}</span>;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10) {
    t12 = <div className="flex items-center justify-between gap-2 rounded-panel-row border border-border-brand bg-background-brand px-4 py-3">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t7 || $[26] !== t8) {
    t13 = <div className="flex flex-col gap-2">{t7}{t8}{t12}</div>;
    $[24] = t12;
    $[25] = t7;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== onOpenTerms || $[29] !== tr) {
    let t15;
    if ($[31] !== onOpenTerms) {
      t15 = chunks => <button type="button" data-testid="prize-terms-link" onClick={onOpenTerms} className="underline underline-offset-2 transition-colors hover:text-foreground-primary">{chunks}</button>;
      $[31] = onOpenTerms;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    t14 = tr.rich("prizeModal.raffleNote", {
      terms: t15
    });
    $[28] = onOpenTerms;
    $[29] = tr;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <p className="text-micro text-foreground-tertiary">{t14}</p>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t13 || $[36] !== t15) {
    t16 = <div data-testid="prize-modal-content" className="flex flex-col gap-4">{t13}{t15}</div>;
    $[35] = t13;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== onOpenChange || $[39] !== open || $[40] !== t1 || $[41] !== t16 || $[42] !== t2) {
    t17 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" title={t1} description={t2}>{t16}</SilkSheet>;
    $[38] = onOpenChange;
    $[39] = open;
    $[40] = t1;
    $[41] = t16;
    $[42] = t2;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
}